import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Upload, FileText, Download, Trash2, AlertCircle } from 'lucide-react'
import { buildApiUrl } from '@/config/api'
import toast from 'react-hot-toast'

interface FileAttachment {
  key: string
  filename: string
  uploadedBy?: string
  canDelete: boolean
}

interface FileUploadProps {
  rfpId: string | null
  initialAttachments?: FileAttachment[]
  onAttachmentsChange: (attachments: FileAttachment[]) => void
}

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/jpg'
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export function FileUpload({ rfpId, initialAttachments = [], onAttachmentsChange }: FileUploadProps) {
  const [attachments, setAttachments] = useState<FileAttachment[]>(initialAttachments)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateAttachments = (newAttachments: FileAttachment[]) => {
    setAttachments(newAttachments)
    onAttachmentsChange(newAttachments)
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 10MB limit`
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type not allowed. Allowed types: PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG`
    }

    return null
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    if (!rfpId) {
      toast.error('Please create the RFP first before uploading files')
      return
    }

    for (const file of files) {
      const validationError = validateFile(file)
      if (validationError) {
        toast.error(`${file.name}: ${validationError}`)
        continue
      }

      console.log('Uploading file:', { 
        name: file.name, 
        type: file.type, 
        size: file.size 
      })
      await uploadFile(file)
    }

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const uploadFile = async (file: File) => {
    setIsUploading(true)
    setUploadProgress(prev => ({ ...prev, [file.name]: 0 }))

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      // Step 1: Get pre-signed upload URL
      const uploadUrlResponse = await fetch(buildApiUrl('/api/files/upload-url'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rfpId,
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }),
      })

      if (!uploadUrlResponse.ok) {
        const errorData = await uploadUrlResponse.json()
        throw new Error(errorData.error || 'Failed to get upload URL')
      }

      const { data: uploadData } = await uploadUrlResponse.json()
      setUploadProgress(prev => ({ ...prev, [file.name]: 25 }))

      // Step 2: Upload file to S3 using pre-signed URL
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      })

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text()
        console.error('S3 Upload failed:', {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          headers: Object.fromEntries(uploadResponse.headers.entries()),
          body: errorText
        })
        throw new Error(`Failed to upload file to storage (${uploadResponse.status}: ${uploadResponse.statusText})`)
      }

      setUploadProgress(prev => ({ ...prev, [file.name]: 75 }))

      // Step 3: Confirm upload with backend
      const confirmResponse = await fetch(buildApiUrl('/api/files/confirm-upload'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rfpId,
          key: uploadData.key,
          filename: file.name,
          size: file.size,
          contentType: file.type,
        }),
      })

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json()
        throw new Error(errorData.error || 'Failed to confirm upload')
      }

      setUploadProgress(prev => ({ ...prev, [file.name]: 100 }))

      // Refresh attachments list
      await refreshAttachments()
      
      toast.success(`${file.name} uploaded successfully!`)
    } catch (error) {
      console.error('Upload error:', error)
      toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsUploading(false)
      setUploadProgress(prev => {
        const newProgress = { ...prev }
        delete newProgress[file.name]
        return newProgress
      })
    }
  }

  const refreshAttachments = async () => {
    if (!rfpId) return
    
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(buildApiUrl(`/api/files/list/${rfpId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        updateAttachments(data.data.files)
      }
    } catch (error) {
      console.error('Error refreshing attachments:', error)
    }
  }

  const handleDownload = async (attachment: FileAttachment) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl(`/api/files/download/${rfpId}/${attachment.key}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to get download URL')
      }

      const { data } = await response.json()
      
      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank')
      
    } catch (error) {
      console.error('Download error:', error)
      toast.error(`Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleDelete = async (attachment: FileAttachment) => {
    if (!confirm(`Are you sure you want to delete ${attachment.filename}?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl('/api/files/delete'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          rfpId,
          key: attachment.key,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to delete file')
      }

      // Refresh attachments list
      await refreshAttachments()
      
      toast.success(`${attachment.filename} deleted successfully!`)
    } catch (error) {
      console.error('Delete error:', error)
      toast.error(`Failed to delete file: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'pdf':
        return <FileText className="h-5 w-5 text-red-500" />
      case 'doc':
      case 'docx':
        return <FileText className="h-5 w-5 text-blue-500" />
      case 'xls':
      case 'xlsx':
        return <FileText className="h-5 w-5 text-green-500" />
      case 'txt':
        return <FileText className="h-5 w-5 text-gray-500" />
      case 'png':
      case 'jpg':
      case 'jpeg':
        return <FileText className="h-5 w-5 text-purple-500" />
      default:
        return <FileText className="h-5 w-5 text-gray-500" />
    }
  }

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">File Attachments</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !rfpId}
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Files
          </Button>
        </div>

        {/* Info message when RFP not created yet */}
        {!rfpId && (
          <div className="flex items-center space-x-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
            <AlertCircle className="h-4 w-4" />
            <span>Save your RFP first to enable file uploads</span>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Upload progress */}
        {Object.entries(uploadProgress).map(([filename, progress]) => (
          <div key={filename} className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Uploading {filename}...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ))}

        {/* File list */}
        {attachments.length > 0 ? (
          <div className="space-y-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.key}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  {getFileIcon(attachment.filename)}
                  <div>
                    <p className="text-sm font-medium">{attachment.filename}</p>
                    {attachment.uploadedBy && (
                      <p className="text-xs text-gray-500">
                        Uploaded by {attachment.uploadedBy}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(attachment)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  
                  {attachment.canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(attachment)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-sm">No files attached</p>
            {rfpId && (
              <p className="text-xs mt-2">
                Click "Upload Files" to add documents, images, or other files to this RFP
              </p>
            )}
          </div>
        )}

        {/* File type info */}
        <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg">
          <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-700">
            <p className="font-medium mb-1">Supported file types:</p>
            <p>PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG (max 10MB each)</p>
          </div>
        </div>
      </div>
    </Card>
  )
}
